import { sqliteTable, text, real, index } from "drizzle-orm/sqlite-core";
import { priceLists } from "./price-lists";

// ============================================================================
// price_list_items — Item rates within a price list (Epic #444)
//
// Each row says: "in price list P, item I (optionally restricted to warehouse
// W) sells for `rate` when the order qty >= `min_qty`, between valid_from
// and valid_to". The (priceListId, itemId, warehouseId, min_qty, valid_from)
// combination uniquely identifies a tier row, but we DO NOT enforce
// uniqueness at the DB layer — by design.
//
// Append-only revision history: rate changes MUST insert a new row with a
// new `valid_from` and (typically) update the previous row's `valid_to` to
// the day before. This preserves an audit trail; the resolver picks the
// most recent row whose validity window contains `date`.
//
// Resolver tie-breaks (`getItemPrice`):
//   1. Filter by date (valid_from <= date <= valid_to, NULLs = open-ended)
//   2. Filter by qty (qty >= min_qty); rows with min_qty larger than qty
//      are skipped — supports "buy 10+ get a discount" tier pricing.
//   3. Prefer warehouse match: if a row matches the requested warehouseId
//      AND a generic (warehouseId IS NULL) row exists, the warehouse-
//      specific row wins. The generic row is the fallback.
//   4. Prefer the highest min_qty bucket the order qualifies for (so a
//      "min 10" tier beats the generic "min 0" row when qty=12).
//   5. Prefer the most recent valid_from (latest published price wins).
//
// FK to price_lists has ON DELETE no action — soft-deleting a price list
// preserves item rows for historical lookup. The resolver filters out
// rows whose parent list has `deletedAt IS NOT NULL` at the service layer.
// ============================================================================
export const priceListItems = sqliteTable("accounting_price_list_items", {
  id: text("id").primaryKey(), // ULID
  priceListId: text("price_list_id").notNull().references(() => priceLists.id),
  // Soft FK to accounting_items.id (no DB constraint to keep cross-table
  // dependency loose — items.ts is in the same module so they always load
  // together but this matches the soft-FK pattern used elsewhere).
  itemId: text("item_id").notNull(),
  // Optional warehouse restriction. NULL = applies to all warehouses
  // (the generic fallback row). Soft FK to inventory_warehouses.id.
  warehouseId: text("warehouse_id"),
  rate: real("rate").notNull(),
  // Tier pricing — minimum order qty this row applies to. Default 0 means
  // "any qty"; a "buy 10+" tier sets min_qty = 10. Resolver picks the
  // highest min_qty bucket the order qualifies for.
  minQty: real("min_qty").notNull().default(0),
  // Validity window. NULLs are open-ended. Append-only edits update the
  // previous row's valid_to and insert a new row with a fresh valid_from.
  validFrom: text("valid_from"), // YYYY-MM-DD
  validTo: text("valid_to"),     // YYYY-MM-DD inclusive
  createdAt: text("created_at").notNull(),
  createdBy: text("created_by"),
}, (table) => ({
  // Hot path: resolver scans by (priceListId, itemId) and filters by
  // warehouse + qty + date in code.
  listItemIdx: index("pli_list_item_idx").on(table.priceListId, table.itemId),
  itemLookupIdx: index("pli_item_idx").on(table.itemId),
}));

export type PriceListItem = typeof priceListItems.$inferSelect;
export type NewPriceListItem = typeof priceListItems.$inferInsert;
